import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Stack, Typography,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { filterActiveInvoices } from '../../../../../utils/invoiceVisibility';

const readRelationships = (raw) => {
  if (raw && typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch (_) { return {}; }
};

export const isDepositForSalesOrderInvoiceSummary = (invoice = {}, depositInvoiceId = null) => {
  const relationships = readRelationships(invoice.invoiceRelationships);
  const roles = [relationships.role, relationships.type, relationships.invoiceRole]
    .map((value) => String(value || '').trim().toLowerCase());
  return roles.some((role) => ['deposit', 'deposit-invoice', 'deposit_request', 'depositrequest', 'full_upfront'].includes(role)) ||
    String(relationships.paymentClassification || '').trim().toLowerCase() === 'full_upfront' ||
    (depositInvoiceId != null && String(invoice.id) === String(depositInvoiceId));
};

// Read-only status for Sales Order -> Actions -> Create Invoice.
// A deposit does not mean the operational Sales Order has been invoiced.
// Full-upfront requests count only when they cover the entire approved
// order, or a paid legacy deposit demonstrably settled the whole order.
export const resolveSalesOrderInvoiceActionState = ({
  invoices = [], instanceUniqueId = '', salesOrderId = null,
  depositInvoiceId = null, approvedSalesOrderTotal = 0,
  requireUpfrontPayment = false, upfrontMode = '',
} = {}) => {
  const selectedSalesOrderId = String(salesOrderId ?? '').trim();
  if (!selectedSalesOrderId) return { created: false, invoiceId: null };
  const approvedCents = Math.round(Number(approvedSalesOrderTotal || 0) * 100);
  const valid = filterActiveInvoices(invoices).filter((invoice) =>
    !invoice?.attachedInstance ||
    String(invoice.attachedInstance) === String(instanceUniqueId)
  );
  const matching = valid.filter((invoice) => {
    const rel = readRelationships(invoice.invoiceRelationships);
    const linkedSalesOrder = rel.salesOrderId ?? rel.sourceSalesOrderDocumentId ??
      rel.salesOrderRevisionId ?? rel.sourceSalesOrderId;
    // Do not show a verified check for an unlinked legacy invoice or for
    // another Sales Order revision on the same project.
    return linkedSalesOrder != null &&
      String(linkedSalesOrder) === selectedSalesOrderId;
  });
  const matches = matching.filter((invoice) => {
    const deposit = isDepositForSalesOrderInvoiceSummary(invoice, depositInvoiceId);
    if (!deposit) return true;
    const rel = readRelationships(invoice.invoiceRelationships);
    const isFullUpfront = [rel.paymentClassification, rel.type, rel.role]
      .some((value) => String(value || '').trim().toLowerCase() === 'full_upfront') ||
      (requireUpfrontPayment === true && String(upfrontMode || '').trim().toLowerCase() === 'full');
    const paid = [invoice.paymentStatus, invoice.invoiceStatus]
      .some((value) => ['paid', 'completed', 'success', 'succeeded']
        .includes(String(value || '').trim().toLowerCase()))
      || Number(invoice.paymentReceivedAmount || 0) >= Number(approvedSalesOrderTotal || 0);
    const invoiceCents = Math.round(Number(invoice.amount || 0) * 100);
    // A 50% deposit, whether paid or pending, never marks the operational
    // invoice created. Nor does an erroneously tagged 100% small deposit.
    return approvedCents > 0 && invoiceCents >= approvedCents &&
      (isFullUpfront || paid);
  }).sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
  const latest = matches[0] || null;
  return {
    created: Boolean(latest),
    invoiceId: latest?.id || null,
    isFullUpfront: Boolean(latest &&
      isDepositForSalesOrderInvoiceSummary(latest, depositInvoiceId)),
    count: matches.length,
  };
};

const isPaid = (invoice = {}) =>
  ['paid', 'completed', 'success', 'succeeded'].includes(
    String(invoice.paymentStatus || '').trim().toLowerCase()
  ) || ['paid', 'completed'].includes(
    String(invoice.invoiceStatus || '').trim().toLowerCase()
  );

const money = (value, currency = 'CAD') => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? `${currency} ${amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : 'Amount unavailable';
};

export const describeSalesOrderInvoiceHistory = ({
  invoices = [], instanceUniqueId = '', salesOrderId = null, depositInvoiceId = null, balanceStatus = null,
} = {}) => {
  const localInvoices = (Array.isArray(invoices) ? invoices : []).filter((invoice) =>
    !invoice?.attachedInstance ||
    String(invoice.attachedInstance) === String(instanceUniqueId)
  );
  const active = filterActiveInvoices(localInvoices);
  const inactiveCount = localInvoices.length - active.length;
  const deposits = active.filter((invoice) => isDepositForSalesOrderInvoiceSummary(invoice, depositInvoiceId));
  const nonDeposits = active.filter((invoice) => !isDepositForSalesOrderInvoiceSummary(invoice, depositInvoiceId));
  const balanceSourceMatches = balanceStatus &&
    (!balanceStatus.latestSalesOrderId || !salesOrderId ||
      String(balanceStatus.latestSalesOrderId) === String(salesOrderId));
  const balance = balanceSourceMatches &&
    Number.isFinite(Number(balanceStatus.currentOrderTotal)) &&
    Number.isFinite(Number(balanceStatus.activeInvoiceTotal)) &&
    Number.isFinite(Number(balanceStatus.uninvoicedAdditionalBalance))
    ? {
      orderTotal: Number(balanceStatus.currentOrderTotal),
      covered: Number(balanceStatus.activeInvoiceTotal),
      remaining: Number(balanceStatus.uninvoicedAdditionalBalance),
    } : null;

  return {
    active: [...active].sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
    activeCount: active.length,
    nonDepositCount: nonDeposits.length,
    depositCount: deposits.length,
    inactiveCount,
    balance,
    canSeeResetCandidate: nonDeposits.length > 0,
  };
};

export default function SalesOrderExistingInvoices({
  invoices = [], instanceUniqueId = '', salesOrderId = null, depositInvoiceId = null, currency = 'CAD',
  balanceStatus = null, historyError = false, balanceError = false,
  onViewInvoices, onOpenInvoice,
}) {
  const details = describeSalesOrderInvoiceHistory({
    invoices, instanceUniqueId, salesOrderId, depositInvoiceId, balanceStatus,
  });
  const { active, activeCount, depositCount, inactiveCount, balance } = details;
  const hasHistory = activeCount > 0 || inactiveCount > 0;
  const [expanded, setExpanded] = useState(false);
  const onlyDeposit = activeCount > 0 && depositCount === activeCount;

  if (!hasHistory && !historyError && !balanceError) return null;

  return (
    <Box
      data-testid="so-existing-invoices-summary"
      sx={{ mb: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 1.25, sm: 1.5 }, bgcolor: 'background.paper' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <ReceiptLongOutlinedIcon color="action" sx={{ fontSize: 19 }} />
          <Typography variant="body2" sx={{ fontWeight: 750 }}>
            {onlyDeposit ? (active.every(isPaid) ? 'Paid deposit on file' : 'Deposit on file') : activeCount ? 'Existing billing' : 'Previous billing'}
          </Typography>
          {onlyDeposit && active.length === 1 ? (
            <Chip size="small" color="success" variant="outlined"
              label={money(active[0].amount, String(active[0].currency || currency).toUpperCase())} />
          ) : <Chip size="small" label={`${activeCount} active · ${inactiveCount} historical`} />}
          {balance && (
            <Typography variant="body2" sx={{ fontWeight: 750 }}>
              {money(balance.remaining, currency)} remaining
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button size="small" onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded} sx={{ textTransform: 'none' }}>
            {expanded ? 'Hide details' : 'View details'}
          </Button>
          {typeof onViewInvoices === 'function' && expanded && (
            <Button size="small" onClick={onViewInvoices} sx={{ textTransform: 'none' }}>Invoice history</Button>
          )}
        </Stack>
      </Stack>
      {expanded && (
        <>
      {active.slice(0, 4).map((invoice) => {
        const deposit = isDepositForSalesOrderInvoiceSummary(invoice, depositInvoiceId);
        const paid = isPaid(invoice);
        const currency = String(invoice.currency || 'CAD').toUpperCase();
        return (
          <Stack
            key={invoice.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            sx={{ py: 0.55, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Invoice #{invoice.id}</Typography>
                <Chip size="small" variant="outlined" label={deposit ? 'Deposit' : 'Invoice'} sx={{ height: 21 }} />
                <Chip size="small" color={paid ? 'success' : 'default'} variant="outlined"
                  label={paid ? 'Paid' : String(invoice.invoiceStatus || invoice.paymentStatus || 'Open')} sx={{ height: 21 }} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                {money(invoice.amount, currency)}
              </Typography>
            </Box>
            {typeof onOpenInvoice === 'function' ? (
              <Button size="small" endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={() => onOpenInvoice(invoice.id)}
                sx={{ textTransform: 'none' }}>View invoice</Button>
            ) : null}
          </Stack>
        );
      })}
      {activeCount > 4 ? (
        <Typography variant="caption" color="text.secondary">And {activeCount - 4} more active invoices in the Invoice tab.</Typography>
      ) : null}
      {inactiveCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {inactiveCount} previous voided/inactive invoice{inactiveCount === 1 ? '' : 's'} retained in history.
        </Typography>
      )}
      {balance ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" flexWrap="wrap" gap={{ xs: 1.5, sm: 3 }} justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary">Approved Sales Order total</Typography>
              <Typography variant="body2" fontWeight={700}>{money(balance.orderTotal, currency)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Covered by active/paid invoices</Typography>
              <Typography variant="body2" fontWeight={700}>{money(balance.covered, currency)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Still available to invoice</Typography>
              <Typography variant="body2" fontWeight={800}>{money(balance.remaining, currency)}</Typography>
            </Box>
          </Stack>
          {balance.remaining <= 0 && activeCount > 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 650 }}
              data-testid="so-invoice-fully-covered">
              Fully invoiced. No standard invoice balance remains.
            </Typography>
          ) : null}
          {balance.remaining > 0 && activeCount > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              A new invoice can be issued for up to the remaining amount; existing invoice amounts stay unchanged.
            </Typography>
          ) : null}
        </>
      ) : null}
      {depositCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Deposits are protected from Reset Invoiced.
        </Typography>
      )}
      {historyError ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Invoice history could not be refreshed. The entries above may be outdated; review the Invoice tab before creating another invoice.
        </Alert>
      ) : null}
      {balanceError || (activeCount > 0 && !balance) ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          The current invoiceable balance could not be verified against this Sales Order. Please check the Invoice tab before proceeding.
        </Alert>
      ) : null}
        </>
      )}
    </Box>
  );
}
